import { ApiTemplateCustomization, EnumValueType } from '@Model';
import type { GroupedCustomization, GroupedCustomizations, ProcessedCustomization } from './customizationGrouping';

/**
 * Creates a unique, dot-concatenated string key for a customization item.
 * @param customization The processed customization item.
 * @returns A unique string key, e.g., 'dataset.group.keyword'.
 */
export const getKey = (customization: ProcessedCustomization): string =>
    `${customization.dataset}.${customization.group}${customization.keyword ? `.${customization.keyword}` : ''}`;

/**
 * Transforms the nested `GroupedCustomizations` structure into a flat
 * map (record) intended for state management.
 * @param groupedCustomizations The nested customizations object.
 * @returns A flat record mapping a unique key to each customization item.
 */
export const flattenGroupedCustomizations = (
    groupedCustomizations: GroupedCustomizations,
): Record<string, ProcessedCustomization> => {
    const values: Record<string, ProcessedCustomization> = {};

    const processItems = (items: GroupedCustomization[]) => {
        for (const item of items) {
            if ('control' in item) {
                processItems(item.customizations); // Recurse into control groups
            } else {
                values[getKey(item)] = item;
            }
        }
    };

    const allItems = Object.values(groupedCustomizations).flatMap((groups) => Object.values(groups).flat());
    processItems(allItems);

    return values;
};

/**
 * Checks if a customization has a meaningful value, ignoring empty strings and placeholder HTML.
 * @param customization The processed customization item to check.
 * @returns `true` if the customization has a meaningful value, otherwise `false`.
 */
export const customizationHasValue = (customization: ApiTemplateCustomization): boolean => {
    const value = customization.source?.trim();

    if (!value) {
        return false;
    }

    // Treat an empty paragraph tag in an HTML field as having no value.
    if (customization.type === EnumValueType.Html && value === '<p></p>') {
        return false;
    }

    return true;
};
